import "./polyfills";
import { createRoot } from "react-dom/client";
import { PostHogProvider } from "@posthog/react";
import { installTestIdAutoSync } from "@ahead-health/core/system-checks/testMode";
import { buildPosthogOptions, posthogKey } from "./posthogSetup";
import { isAnalyticsSuppressed } from "@/utils/analytics";
import { isOneTimeHandoff, ONE_TIME_HANDOFF } from "@/utils/cartEntryStep";
import App from "./App.tsx";
import "./index.css";

installTestIdAutoSync();

// Simple error boundary for unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
});

// Simple global error handler
window.addEventListener("error", (event) => {
  // Skip script errors without details (CORS issues)
  if (event.message === "Script error." || event.message === "Script error") {
    console.warn("Cross-origin script error detected, continuing execution");
    return true;
  }
  console.error("Global error:", event.error || event.message);
});

// Standard React root initialization
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

// A visit that came in on the membership-first flow's one-off handoff
// (isOneTimeHandoff) carries `handoff_from` on its events, so this flow's
// funnels can read those visitors apart (Eugene, 22.10). Tied to PostHog's
// session, not the tab: register_for_session kept it for the tab's life, so a
// later visit in the same tab, from an email or the site, was tagged too.
// The session id is kept so a reload (its ?products= gone by then) still
// counts. Nothing is added where this flow captures nothing
// (isAnalyticsSuppressed: localhost, the opt-out, test sessions).
const HANDOFF_SESSION_KEY = "ph_handoff_session";
const handoffAtLoad = isOneTimeHandoff() && !isAnalyticsSuppressed();
const handoffSession = (): string | null => {
  try {
    return sessionStorage.getItem(HANDOFF_SESSION_KEY);
  } catch {
    return null;
  }
};

// PostHog's setup lives in posthogSetup.ts, shared with the membership-first
// app so the two cannot drift.
const posthogOptions = buildPosthogOptions();
posthogOptions.before_send = (event) => {
  const session = event?.properties?.$session_id;
  if (!event || !session) return event;
  if (handoffAtLoad) {
    try {
      sessionStorage.setItem(HANDOFF_SESSION_KEY, session);
    } catch {
      // No storage: only this page's events are tagged.
    }
  }
  if (handoffAtLoad || handoffSession() === session) {
    event.properties = {
      ...event.properties,
      handoff_from: ONE_TIME_HANDOFF.value,
    };
  }
  return event;
};

const root = createRoot(rootElement);
root.render(
  posthogKey ? (
    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
      <App />
    </PostHogProvider>
  ) : (
    <App />
  ),
);
